import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "../../Layouts/PageLayout/PageLayout";
import TabsNavigation from "../../components/common/TabsNavigation/TabsNavigation";
import Button from "../../components/common/Button/Button";
import Badge from "../../components/common/Badge/Badge";
import { FiDownload, FiMessageSquare, FiPlay, FiUsers } from "react-icons/fi";
import { MdEuroSymbol } from "react-icons/md";
import { getApiInstance } from "../../utils/api";
import { nombreFicheroDescarga } from "../../utils/descargas";
import Table from "../../components/common/Table/Table";
import {
    CiCircleCheck,
    CiCircleInfo,
    CiClock1,
    CiWarning,
} from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import MapComponent from "../../components/common/MapComponent/MapComponent";
import Modal from "../../components/common/Modal/Modal";
import InputTextarea from "../../components/common/Inputs/InputTextarea/InputTextarea";
import { useZone } from "../../context/ZoneContext";
import { ZONE_LABEL_MAP } from "../../constants/zones";
import { esIntermediario, zonaFijaUsuario } from "../../utils/permisos";
import { getZoneLabel } from "../../utils/zoneLabels";
import "./CentrosProductores.scss";
import ProgressBar from "../../components/common/ProgressBar/ProgressBar";
import { mapList } from "../../utils/utils";
import { IoStopOutline, IoAdd } from "react-icons/io5";
import { LuWeight } from "react-icons/lu";
import { formatDate } from "../../utils/utils";
import { useTranslation } from "react-i18next";

export default function CentrosProductores({ user }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tableRef = useRef(null);
    /* Como en la antigua (Obras/Index.cshtml): añade centros cualquier perfil con
       acceso de gestión salvo el intermediario. El centro queda en la zona del
       usuario (el servidor la fija). */
    const canAdd =
        Number(user.codAcceso) === 1 && !esIntermediario(user.role);
    const [activeTab, setActiveTab] = useState("listado");
    const [obras, setObras] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedZone } = useZone();

    const spaceCodeLabel = t("productionCenters.columns.spaceCode");
    const spaceCodePlaceholder = t("placeholders.enterSpaceCode");
    const communityLabel = t("fields.community");
    const provinceLabel = t("fields.province");
    const localityLabel = t("fields.locality");
    const communityPlaceholder = t("placeholders.selectCommunity");
    const provincePlaceholder = t("placeholders.selectProvince");
    const localityPlaceholder = t("placeholders.enterLocality");
    const [intermediarios, setIntermediarios] = useState([]);
    const [cadenas, setCadenas] = useState([]);
    const [tiposCentro, setTiposCentro] = useState([]);
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [promotores, setPromotores] = useState([]);
    const [usuariosIntermediarios, setUsuariosIntermediarios] = useState([]);
    const [productores, setProductores] = useState([]);
    const [selectedObras, setSelectedObras] = useState(new Set());
    const [accionPendiente, setAccionPendiente] = useState(null);
    const [ejecutandoAccion, setEjecutandoAccion] = useState(false);
    const [accionError, setAccionError] = useState(null);
    const [accionExito, setAccionExito] = useState(null);

    /* Nota del intermediario sobre la obra (Obras.Obs2), como "Añadir/Editar nota"
       del listado de la aplicación antigua: una sola nota por obra, que se ve como
       tooltip del icono de comentario y que solo edita el perfil intermediario. */
    const NOTA_MAX = 500;
    const [notaPendiente, setNotaPendiente] = useState(null);
    const [textoNota, setTextoNota] = useState("");
    const [notaVacia, setNotaVacia] = useState(false);
    const [confirmarBorrarNota, setConfirmarBorrarNota] = useState(false);
    const [guardandoNota, setGuardandoNota] = useState(false);

    const downloadBlob = (response, defaultName) => {
        const fileName = nombreFicheroDescarga(
            response.headers["content-disposition"],
            defaultName
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const [exportando, setExportando] = useState(false);

    const generarInforme = async (cods, defaultName) => {
        if (exportando) return;
        setExportando(true);
        const api = getApiInstance();
 
        try {
            const response = await api.post(
                "/api/Obra/CreateReportByCods",
                cods,
                {
                    responseType: "blob",
                }
            );
            downloadBlob(response, defaultName);
        } catch (err) {
            setError("No se pudo generar el reporte.");
            console.error(err);
        } finally {
            setExportando(false);
        }
    };

    const handleExportarCentro = async (row) => {
        await generarInforme([row.codObra], "Informe_CentroProductor.xlsx");
    };

    const handleExportarCentros = async () => {
        let filasAExportar;

        if (selectedObras && selectedObras.size > 0) {
            // Si hay filas marcadas, exportar solo esas.
            filasAExportar = obras.filter((o) =>
                [...selectedObras].some(
                    (id) => String(id) === String(o.codObra)
                )
            );
        } else {
            // Si no hay seleccion, exportar las obras que muestra la tabla
            // (respetando el filtro Zona y el resto de filtros).
            filasAExportar =
                tableRef.current?.getVisibleRows() ?? [];
        }

        if (filasAExportar.length === 0) {
            setError(
                "No hay obras que exportar con los filtros aplicados."
            );
            return;
        }

        await generarInforme(
            filasAExportar.map((item) => item.codObra),
            "Informe_CentrosProductores.xlsx"
        );
    };

    const accionesObraConfig = {
        cerrar: {
            endpoint: "/api/Obra/CloseObra",
            titulo: "Cerrar obra",
            texto: (row) =>
                `¿Seguro que quieres cerrar la obra ${row.codEspacio}?`,
            confirmar: "Cerrar obra",
            exito: "Obra cerrada correctamente",
        },
        solicitarCierre: {
            endpoint: "/api/Obra/RequestCloseObra",
            titulo: "Solicitar cierre",
            texto: (row) =>
                `¿Seguro que quieres solicitar el cierre de la obra ${row.codEspacio}?`,
            confirmar: "Solicitar cierre",
            exito: "Solicitud de cierre enviada correctamente",
        },
        incorrecto: {
            endpoint: "/api/Obra/MarkIncorrectObra",
            titulo: "Marcar obra como incorrecta",
            texto: (row) =>
                `¿Marcar la obra ${row.codEspacio} como incorrecta?`,
            confirmar: "Marcar incorrecta",
            exito: "Obra marcada como incorrecta",
        },
        quitarIncorrecto: {
            endpoint: "/api/Obra/ClearIncorrectObra",
            titulo: "Quitar marca de incorrecto",
            texto: (row) =>
                `¿Quitar la marca de incorrecto de la obra ${row.codEspacio}?`,
            confirmar: "Quitar incorrecto",
            exito: "Marca de incorrecto retirada",
        },
    };

    const ejecutarAccionObra = async () => {
        if (!accionPendiente || ejecutandoAccion) return;
        const config = accionesObraConfig[accionPendiente.tipo];
        setEjecutandoAccion(true);
        try {
            const api = getApiInstance();
            await api.post(`${config.endpoint}/${accionPendiente.row.codObra}`);
            await fetchObras();
            setAccionPendiente(null);
            setAccionError(null);
            setAccionExito(config.exito);
            setTimeout(() => setAccionExito(null), 4000);
        } catch (err) {
            console.error(err);
            setAccionPendiente(null);
            setAccionExito(null);
            setAccionError(
                "No se pudo completar la acción. Vuelve a intentarlo más tarde."
            );
        } finally {
            setEjecutandoAccion(false);
        }
    };

    const abrirNota = (row) => {
        setNotaPendiente(row);
        setTextoNota(row.obs2 || "");
        setNotaVacia(false);
        setConfirmarBorrarNota(false);
    };

    const cerrarNota = () => {
        if (guardandoNota) return;
        setNotaPendiente(null);
        setConfirmarBorrarNota(false);
    };

    /* Una nota vacía la borra en el back. */
    const guardarNota = async (nota) => {
        if (!notaPendiente || guardandoNota) return;
        setGuardandoNota(true);
        const borrando = nota === "";
        try {
            const api = getApiInstance();
            await api.post(`/api/Obra/SaveNota/${notaPendiente.codObra}`, {
                nota,
            });
            await fetchObras();
            setNotaPendiente(null);
            setConfirmarBorrarNota(false);
            setAccionError(null);
            setAccionExito(
                borrando ? "Nota eliminada correctamente" : "Nota guardada correctamente"
            );
            setTimeout(() => setAccionExito(null), 4000);
        } catch (err) {
            console.error(err);
            setNotaPendiente(null);
            setConfirmarBorrarNota(false);
            setAccionExito(null);
            setAccionError(
                borrando
                    ? "No se pudo eliminar la nota. Vuelve a intentarlo más tarde."
                    : "No se pudo guardar la nota. Vuelve a intentarlo más tarde."
            );
        } finally {
            setGuardandoNota(false);
        }
    };

    const aceptarNota = () => {
        const nota = textoNota.trim();
        if (nota === "") {
            setNotaVacia(true);
            return;
        }
        guardarNota(nota);
    };

    const columnasCentrosProductores = [
        { key: "codObra", label: "ID", visible: false },
        {
            key: "codEspacio",
            label: t("productionCenters.columns.spaceCode"),
            visible: true,
            sortable: true,
            isLink: true,
            render: (row) => {
                const href = `/centros-productores/${row.codObra}`;
                return (
                    <Link className="link-style" to={href}>
                        <span className="table-cell-content__primary table-cell-content__primary--bold">
                            {row.codEspacio}
                        </span>
                    </Link>
                );
            },
        },
        {
            key: "nombre",
            label: t("productionCenters.columns.workCode"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
            render: (row) =>
                row.obs2?.trim() ? (
                    <span className="centro-nota">
                        <span className="centro-nota__icono" title={row.obs2}>
                            <FiMessageSquare aria-label="Nota del intermediario" />
                        </span>
                        {row.nombre || "-"}
                    </span>
                ) : (
                    row.nombre || "-"
                ),
        },
        {
            key: "direccion",
            label: t("fields.address"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "fechaInicio",
            label: t("fields.startDate"),
            visible: true,
            sortable: true,
            type: "date",
            visibleOnExpand: true,
        },
        {
            key: "fechaFin",
            label: t("fields.endDate"),
            visible: true,
            sortable: true,
            type: "date",
            visibleOnExpand: true,
        },
        {
            key: "intermediario",
            label: t("fields.intermediary"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "cadena",
            label: t("fields.chain"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "tipoObra",
            label: t("productionCenters.columns.centerType"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "codEstado",
            label: t("fields.state"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
    ];

    const formElementsCentrosProductores = [
        {
            title: t("sections.generalInfo"),
            titleVisible: "true",
            elements: [
                {
                    name: "codProductor",
                    type: "select",
                    label: t("fields.producer"),
                    placeholder: t("productionCenters.form.selectProducer"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: productores,
                },
                {
                    name: "codTipoObra",
                    type: "select",
                    label: t("productionCenters.columns.centerType"),
                    placeholder: t("productionCenters.form.selectCenterType"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: tiposCentro,
                },
                {
                    name: "codEspacio",
                    type: "text",
                    label: spaceCodeLabel,
                    placeholder: spaceCodePlaceholder,
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "nombre",
                    type: "text",
                    label: t("productionCenters.columns.workCode"),
                    placeholder: t("placeholders.enterWorkCode"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "codObra",
                    type: "text",
                    label: t("productionCenters.columns.workCode"),
                    placeholder: t("placeholders.enterWorkCode"),
                    required: false,
                    disabled: true,
                    labelVisible: false,
                    visible: false,
                },
                {
                    name: "codIntegracion",
                    type: "text",
                    label: t("fields.integrationCode"),
                    placeholder: t("placeholders.enterIntegrationCode"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "fechaInicio",
                    type: "date",
                    label: t("fields.startDate"),
                    placeholder: t(
                        "placeholders.selectDate"
                    ),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "fechaFin",
                    type: "date",
                    label: t("fields.endDate"),
                    placeholder: t(
                        "placeholders.selectDate"
                    ),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "nima",
                    type: "text",
                    label: t("fields.NIMA"),
                    placeholder: t("placeholders.enterNIMA"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "codCadena",
                    type: "select",
                    label: t("fields.chain"),
                    placeholder: t(
                        "productionCenters.form.selectChain"
                    ),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: cadenas,
                },
                {
                    name: "isDemolicion",
                    type: "checkbox",
                    label: t("fields.demolicion"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "codZona",
                    type: "number",
                    label: t("productionCenters.form.zone"),
                    visible: false,
                    /* La zona del usuario; solo el Administrador global usa la
                       zona seleccionada. */
                    defaultValue: zonaFijaUsuario(user) ?? selectedZone.value,
                },
            ],
        },
        {
            title: t("productionCenters.form.responsibles"),
            titleVisible: true,
            elements: [
                {
                    name: "codIntermediario",
                    type: "select",
                    label: t("fields.intermediary"),
                    placeholder: t("productionCenters.form.selectIntermediary"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: intermediarios,
                },
                {
                    name: "codUsuario",
                    type: "select",
                    label: t("fields.users"),
                    placeholder: t("productionCenters.form.selectUser"),
                    options: usuariosIntermediarios,
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "responsable",
                    type: "text",
                    label: t("productionCenters.form.responsible"),
                    placeholder: t("productionCenters.form.enterResponsible"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "mailResponsable",
                    type: "text",
                    label: t("productionCenters.form.emailResponsible"),
                    placeholder: t(
                        "productionCenters.form.enterEmailResponsible"
                    ),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
            ],
        },
        {
            title: t("sections.location"),
            titleVisible: true,
            elements: [
                {
                    name: "direccion",
                    type: "text",
                    label: t("fields.address"),
                    placeholder: t("placeholders.enterAddress"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "codPais",
                    type: "select",
                    label: t("fields.country"),
                    placeholder: t("placeholders.selectCountry"),
                    options: paises,
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "codComunidad",
                    type: "select",
                    label: communityLabel,
                    placeholder: communityPlaceholder,
                    options: comunidades,
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    associatedFormElement: "codPais",
                },
                {
                    name: "codProvincia",
                    type: "select",
                    label: provinceLabel,
                    placeholder: provincePlaceholder,
                    options: provincias,
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    associatedFormElement: "codComunidad",
                },
                {
                    name: "localidad",
                    type: "text",
                    label: localityLabel,
                    placeholder: localityPlaceholder,
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "cp",
                    type: "text",
                    label: t("fields.postalCode"),
                    placeholder: t("placeholders.enterPostalCode"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "codZona",
                    type: "number",
                    label: t("productionCenters.form.zone"),
                    visible: false,
                    /* La zona del usuario; solo el Administrador global usa la
                       zona seleccionada. */
                    defaultValue: zonaFijaUsuario(user) ?? selectedZone.value,
                },
            ],
        },
        {
            title: t("productionCenters.form.centerData"),
            titleVisible: true,
            elements: [
                {
                    name: "codUbicacion",
                    type: "select",
                    label: t("fields.location"),
                    placeholder: t("productionCenters.form.selectLocation"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: [
                        {
                            label: "Calle",
                            value: "1",
                            id: 1,
                        },
                        {
                            label: "Centro comercial",
                            value: "2",
                            id: 2,
                        },
                    ],
                },
                {
                    name: "superficie",
                    type: "number",
                    label: t("productionCenters.form.surface"),
                    placeholder: t("productionCenters.form.enterSurface"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "promotor",
                    type: "select",
                    label: t("productionCenters.form.promoter"),
                    placeholder: t("productionCenters.form.selectPromoter"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: promotores,
                },
                {
                    name: "kilometros1",
                    type: "number",
                    label: t("productionCenters.form.transportMaterialSupply"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "kilometros2",
                    type: "number",
                    label: t("productionCenters.form.transportWasteManagement"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "observaciones",
                    type: "textarea",
                    label: t("fields.observations"),
                    placeholder: t("productionCenters.form.enterObservation"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
            ],
        },
    ];

    const filterFields = [
        {
            key: "codEspacio",
            label: t("productionCenters.columns.spaceCode"),
            type: "multiselect",
        },
        {
            key: "nombre",
            label: t("productionCenters.columns.workCode"),
            type: "multiselect",
        },
        {
            key: "codEstado",
            label: t("fields.state"),
            type: "multiselect",
            labelMap: [
                t("states.noData"),
                t("states.noData"),
                t("states.closed"),
                t("states.pending"),
                t("states.incident"),
                t("states.validated"),
                t("states.closedRequest"),
                t("states.incorrect"),
            ],
        },
        {
            key: "codUbicacion",
            label: t("fields.location"),
            type: "multiselect",
            labelMap: ["", "Calle", "Centro comercial"],
        },
        {
            /* La empresa de mano de obra. Se filtra por el nombre y no por
               codIntermediario porque las opciones del filtro rapido se sacan de
               los valores de las filas: con el codigo saldrian numeros. Sin
               `advanced: true`, sale en la barra principal y no en el avanzado. */
            key: "intermediario",
            label: t("fields.intermediaryCompany"),
            type: "multiselect",
        },
        {
            key: "fechasInicio",
            label: t("fields.startDate"),
            type: "daterange",
            rangeStartField: "fechaInicio",
            rangeEndField: "fechaInicio",
        },
        {
            key: "fechas",
            label: t("fields.endDate"),
            type: "daterange",
            rangeStartField: "fechaInicio",
            rangeEndField: "fechaFin",
        },
        {
            key: "codZona",
            label: "Zona",
            type: "multiselect",
            labelMap: ZONE_LABEL_MAP,
        },
        { key: "tipoObra", 
            label: "Tipo de obra", 
            type: "enum" },
    ];

    const cumlimientosStates = [
        { label: "states.noData", type: "info", Icon: CiClock1 },
        { label: "states.noData", type: "info", Icon: CiClock1 },
        { label: "states.closed", type: "success", Icon: CiCircleCheck },
        { label: "states.pending", type: "indigo", Icon: CiClock1 },
        { label: "states.incident", type: "warning", Icon: CgDanger },
        { label: "states.validated", type: "success", Icon: CiCircleCheck },
        { label: "states.closedRequest", type: "purple", Icon: CiClock1 },
        { label: "states.incorrect", type: "danger", Icon: CgDanger },
    ];

    const tabs = [
        { id: "listado", label: t("productionCenters.tabs.list") },
        { id: "mapa", label: t("productionCenters.tabs.map") },
    ];

    // Función para obtener el porcentaje de valorización único para cada centro
    const getValorizacionStatus = (centro) => {
        // Si viene el porcentaje real de la API, usarlo
        if (
            centro.porcentajeValorizacion !== undefined &&
            centro.porcentajeValorizacion !== null
        ) {
            return Math.max(0, Math.min(100, centro.porcentajeValorizacion));
        }

        // Generar porcentaje único basado en el ID del centro
        const id = centro.codObra || centro.id || centro.codEspacio || "0";
        let hash = 0;
        const str = id.toString();

        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // Convert to 32bit integer
        }

        // Convertir hash a porcentaje entre 0-100
        const percentage = Math.abs(hash) % 101;
        return percentage;
    };

    // Función para obtener el estado de cumplimiento
    const getCumplimientoStatus = (centro) => {
        const cod =
            typeof centro.codEstado === "number"
                ? String(centro.codEstado)
                : String(centro.codEstado ?? "");

        switch (cod) {
            case "2":
            case "5":
            case "COMPLETADO":
            case "VALIDADO":
                return {
                    status: t("states.validated"),
                    variant: "success",
                    Icon: CiCircleCheck,
                };
            case "4":
            case "7":
            case "INCIDENCIA":
            case "RETRASADO":
                return {
                    status: t("states.incident"),
                    variant: "danger",
                    Icon: CgDanger,
                };
            case "3":
            case "6":
            case "PENDIENTE":
            case "EN_PROGRESO":
                return {
                    status: t("states.pending"),
                    variant: "warning",
                    Icon: CiWarning,
                };
            default:
                return {
                    status: t("states.noData"),
                    variant: "outlined",
                    Icon: CiCircleInfo,
                };
        }
    };

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
    };

    const fetchObras = async () => {
        setLoading(true);
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Obra/GetObrasFromUser/es`
            );

            setObras(response.data);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las obras. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchIntermediarios = async () => {
        const api = getApiInstance();
        try {
            let intermediariosData = [];

            const response = await api.get(
                `/api/Intermediarios/GetIntermediarios`
            );
            intermediariosData = response.data;

            setIntermediarios([
                ...mapList(intermediariosData, "nombre", "codIntermediario"),
            ]);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchCadenas = async () => {
        const api = getApiInstance();
        try {
            let cadenasData = [];

            const response = await api.get(`/api/Cadenas/GetAllCadenas`);
            cadenasData = response.data;

            setCadenas([
                ...mapList(cadenasData, "cadena", "codCadena", "codCadena"),
            ]);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchTiposObra = async () => {
        const api = getApiInstance();
        try {
            let tiposObraData = [];

            const response = await api.get(`/api/ObrasTipo/GetAllObrasTipo`);
            tiposObraData = response.data;

            setTiposCentro([
                ...mapList(
                    tiposObraData,
                    "descripcion",
                    "codTipoObra",
                    "codTipoObra"
                ),
            ]);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchComunidades = async () => {
        const api = getApiInstance();

        try {
            let comunidadesData = [];

            const response = await api.get(
                `/api/Comunidades/GetComunidades`
            );
            comunidadesData = response.data;

            setComunidades(
                mapList(
                    comunidadesData,
                    "comunidad",
                    "codComunidad",
                    "codComunidad",
                    "codPais"
                )
            );

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchPaises = async () => {
        const api = getApiInstance();

        try {
            let paisesData = [];

            const response = await api.get(
                `/api/Paises/GetPaises`
            );
            paisesData = response.data;

            setPaises(mapList(paisesData, "pais", "codPais", "codPais"));
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los países. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchProvincias = async () => {
        const api = getApiInstance();
        try {
            let provinciasData = [];

            const response = await api.get(
                `/api/Provincias/GetProvincias`
            );
            provinciasData = response.data;

            setProvincias(
                mapList(
                    provinciasData,
                    "provincia",
                    "codProvincia",
                    "codProvincia",
                    "codComunidad"
                )
            );
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchPromotores = async () => {
        const api = getApiInstance();
        try {
            let promotoresData = [];

            const response = await api.get(`/api/Promotor/GetPromotores`);
            promotoresData = response.data;

            setPromotores(
                mapList(promotoresData, "promotor", "promotor", "promotor")
            );
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchUsuariosIntermediarios = async () => {
        const api = getApiInstance();
        try {
            let usuariosIntermediariosData = [];

            const response = await api.get(
                `/api/Usuario/GetUsuarios`
            );
            usuariosIntermediariosData = response.data;

            setUsuariosIntermediarios(
                mapList(
                    usuariosIntermediariosData,
                    ["nombre", "apellidos"],
                    "codUsuario",
                    "codUsuario"
                )
            );
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchProductores = async () => {
        const api = getApiInstance();
        try {
            let productoresData = [];

            const response = await api.get(
                `/api/Productores/GetProductores`
            );
            productoresData = response.data;

            // Hay productores con el mismo nombre en distintas zonas: la zona
            // entre paréntesis permite distinguirlos en el desplegable.
            setProductores(
                mapList(productoresData, "nombre", "codProductor").map(
                    (opcion, i) => {
                        const zona =
                            ZONE_LABEL_MAP[productoresData[i].codZona];
                        return zona
                            ? { ...opcion, label: `${opcion.label} (${zona})` }
                            : opcion;
                    }
                )
            );
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const loadAll = async () => {
            try {
                await fetchObras();

                await Promise.all([
                    fetchIntermediarios(),
                    fetchCadenas(),
                    fetchTiposObra(),
                    fetchPaises(),
                    fetchComunidades(),
                    fetchProvincias(),
                    fetchPromotores(),
                    fetchUsuariosIntermediarios(),
                    fetchProductores(),
                ]);
            } catch (err) {
                if (isMounted) {
                    setError(
                        "No se han podido cargar los datos. Vuelve a intentarlo más tarde."
                    );
                }
                console.error("Error en loadAll:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAll();

        return () => {
            isMounted = false;
        };
    }, []);

    const modalMap = (centro) => {
        return (
            <div className="centro-map-details">
                <div className="centro-header">
                    <h3 className="centro-title">
                        {centro.nombre || centro.codEspacio || ""},{" "}
                        {centro.direccion || ""}
                    </h3>
                </div>

                <div className="centro-info-section">
                    <div className="info-row">
                        <FiPlay className="info-icon" />
                        <span className="info-label">
                            {t("fields.startDate")}
                        </span>
                        <span className="info-value">
                            {formatDate(centro.fechaInicio) || ""}
                        </span>
                    </div>
                    <div className="info-row">
                        <IoStopOutline className="info-icon" />
                        <span className="info-label">
                            {t("fields.endDate")}
                        </span>
                        <span className="info-value">
                            {formatDate(centro.fechaFin) || ""}
                        </span>
                    </div>

                    <div className="info-row info-row--highlight">
                        <FiUsers className="info-icon" />
                        <span className="info-label">
                            {t("fields.intermediary")}
                        </span>
                        <span className="info-value intermediario-link">
                            {centro.intermediario || ""}
                        </span>
                    </div>

                    <div className="info-row">
                        <LuWeight className="info-icon" />
                        <span className="info-label">
                            {t("nav.generatedQuantities")}
                        </span>
                        <span className="info-value">{centro.peso || ""}</span>
                    </div>

                    <div className="info-row">
                        <MdEuroSymbol className="info-icon" />
                        <span className="info-label">{t("nav.costs")}</span>
                        <span className="info-value">
                            {centro.costeTotal || ""}
                        </span>
                    </div>
                </div>

                <div className="centro-valorizacion-section">
                    <h4 className="section-title">
                        {t("sheets.center.charts.valorization")}
                    </h4>
                    <div className="valorizacion-percentage">
                        {(centro.peso
                            ? (centro.valorizacion / centro.peso) * 100
                            : 0
                        ).toFixed(2)}
                        %
                    </div>
                    <ProgressBar
                        percentage={
                            (centro.peso
                                ? (centro.valorizacion / centro.peso) * 100
                                : 0
                            ).toFixed(2) || 0
                        }
                        height={8}
                        width="100%"
                    />
                </div>

                <div className="centro-cumplimiento-section">
                    <div className="cumplimiento-header">
                        <h4 className="section-title">
                            {t("sections.legalCompliance")}
                        </h4>
                        <Badge
                            variant={getCumplimientoStatus(centro).variant}
                            Icon={getCumplimientoStatus(centro).Icon}
                        >
                            {getCumplimientoStatus(centro).status}
                        </Badge>
                        {centro.cumplimientos.map((cumplimiento, index) => (
                            <div key={index} className="cumplimiento-item">
                                <span className="cumplimiento-name">
                                    {cumplimiento.nombre || ""}
                                </span>
                                <Badge
                                    variant={
                                        cumlimientosStates[
                                            cumplimiento.codEstado
                                        ]?.type || "info"
                                    }
                                >
                                    {t(
                                        cumlimientosStates[
                                            cumplimiento.codEstado
                                        ]?.label
                                    ) || t("states.noData")}
                                </Badge>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="modal-actions">
                    <Button
                        variant="filled"
                        onClick={() => {
                            navigate(`/centros-productores/${centro.codObra}`);
                        }}
                    >
                        {t("common.view")} {t("sheets.center.title")}
                    </Button>
                </div>
            </div>
        );
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case "listado":
                return (
                    <Table
                        ref={tableRef}
                        hideAddButton={true}
                        columns={columnasCentrosProductores}
                        dataTable={obras}
                        showCheckboxes={true}
                        noResultsMessage={t("productionCenters.noCentersFound")}
                        actions={(row) => {
                            const estado = Number(row.codEstado);
                            const esCerrado = estado === 2;
                            const esIncorrecto = estado === 7;
                            const esSolicitudCierre = estado === 6;
                            const esIntermediario = Number(user.role) === 4;
                            const acciones = [];
                            if (!esCerrado && !esIncorrecto) {
                                acciones.push("Editar");
                            }
                            acciones.push({
                                label: "Exportar Información",
                                icon: FiDownload({}),
                                action: (r) =>
                                    handleExportarCentro(r),
                            });
                            /* Sin danger: la tabla oculta al perfil 4 las acciones "danger". */
                            if (esIntermediario) {
                                acciones.push({
                                    label: row.obs2?.trim()
                                        ? "Editar nota"
                                        : "Añadir nota",
                                    icon: FiMessageSquare({}),
                                    action: (r) => abrirNota(r),
                                });
                            }
                            if (!esCerrado && !esIncorrecto) {
                                /* El intermediario no puede cerrar directamente: solicita el cierre
                                   y el back avisa a los responsables de la zona. */
                                if (esIntermediario) {
                                    if (!esSolicitudCierre) {
                                        acciones.push({
                                            label: "Solicitar Cierre",
                                            icon: IoStopOutline({}),
                                            action: (r) =>
                                                setAccionPendiente({
                                                    tipo: "solicitarCierre",
                                                    row: r,
                                                }),
                                        });
                                    }
                                } else {
                                    acciones.push({
                                        label: "Cerrar Obra",
                                        icon: IoStopOutline({}),
                                        action: (r) =>
                                            setAccionPendiente({
                                                tipo: "cerrar",
                                                row: r,
                                            }),
                                    });
                                }
                            }
                            if (esIncorrecto) {
                                acciones.push({
                                    label: "Quitar Incorrecto",
                                    icon: CgDanger({}),
                                    danger: true,
                                    action: (r) =>
                                        setAccionPendiente({
                                            tipo: "quitarIncorrecto",
                                            row: r,
                                        }),
                                });
                            } else if (!esCerrado) {
                                acciones.push({
                                    label: "Incorrecto",
                                    icon: CgDanger({}),
                                    danger: true,
                                    action: (r) =>
                                        setAccionPendiente({
                                            tipo: "incorrecto",
                                            row: r,
                                        }),
                                });
                            }
                            return acciones;
                        }}
                        searchTerms={[
                            "codEspacio",
                            "intermediario",
                            "tipoObra",
                            "direccion",
                        ]}
                        keyProp="codObra"
                        expandable={true}
                        elementName={t("productionCenters.title")}
                        stateProp="codEstado"
                        sortProp="codEspacio"
                        states={[
                            {
                                label: t("states.noData"),
                                type: "info",
                                Icon: CiClock1,
                            },
                            {
                                label: t("states.noData"),
                                type: "info",
                                Icon: CiClock1,
                            },
                            {
                                label: t("states.closed"),
                                type: "success",
                                Icon: CiCircleCheck,
                            },
                            {
                                label: t("states.pending"),
                                type: "indigo",
                                Icon: CiClock1,
                            },
                            {
                                label: t("states.incident"),
                                type: "warning",
                                Icon: CgDanger,
                            },
                            {
                                label: t("states.validated"),
                                type: "success",
                                Icon: CiCircleCheck,
                            },
                            {
                                label: t("states.closedRequest"),
                                type: "purple",
                                Icon: CiClock1,
                            },
                            {
                                label: t("states.incorrect"),
                                type: "danger",
                                Icon: CgDanger,
                            },
                        ]}
                        filterFields={filterFields}
                        loading={loading}
                        formElements={
                            user.role == 1 ? formElementsCentrosProductores : []
                        }
                        addApiUrl="/api/Obra/SetObra"
                        editApiUrl="/api/Obra/UpdateObra"
                        deleteApiUrl="/api/Obra/DeleteObra"
                        selectedRows={selectedObras}
                        onSelectionChange={setSelectedObras}
                        userCodAcceso={Number(user.codAcceso)}
                        userCodPerfil={Number(user.role)}
                        excludeFields={["ProductorObj", "productorObj", "residuos","intermediario", "tipoObra","direccion", "fechaInicio", "fechaFin", "codProductor", "codCadena", "codProvincia", "codPais", "codComunidad", "codUsuario", "iconoEstado", "ubicacion", "codIntegracion", "codTipoObra", "latitud", "longitud", "codIntermediario", "tipoObra_EN", "codEncargado", "agua", "electricidad", "kilometros1", "kilometros2", "isDemolicion", "observaciones", "obs2", "numberManifiesto", "fechaFinAproximado"]}
                    />
                );
            case "mapa":
                return (
                    <MapComponent
                        data={obras}
                        center={[42.0, 2.0]}
                        zoom={5}
                        height="600px"
                        width="100%"
                        modalContent={modalMap}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <PageLayout
            title={t("productionCenters.title")}
            subtitle={t("productionCenters.subtitle")}
            actions={[
                ...(canAdd
                    ? [
                          <Button
                              key="add"
                              variant="filled"
                              startIcon={IoAdd({})}
                              onClick={() => tableRef.current?.openAddModal()}
                              endIcon={undefined}
                          >
                              {`${t("common.add")} ${t(
                                  "productionCenters.title"
                              )}`}
                          </Button>,
                      ]
                    : []),
                <Button
                    key="exportar"
                    variant="filled"
                    startIcon={FiDownload({})}
                    onClick={handleExportarCentros}
                    endIcon={undefined}
                    disabled={exportando}
                >
                    {exportando ? "Generando..." : "Exportar"}
                </Button>,
            ]}
        >
            <TabsNavigation
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />
            {accionError && (
                <div
                    style={{
                        padding: "20px",
                        textAlign: "center",
                        fontSize: "16px",
                        color: "#e74c3c",
                        backgroundColor: "#fdf2f2",
                        border: "1px solid #fecaca",
                        borderRadius: "6px",
                        margin: "10px 0",
                    }}
                >
                    {accionError}
                </div>
            )}
            {accionExito && (
                <div
                    style={{
                        padding: "20px",
                        textAlign: "center",
                        fontSize: "16px",
                        color: "#1e7e34",
                        backgroundColor: "#f0fff4",
                        border: "1px solid #c3e6cb",
                        borderRadius: "6px",
                        margin: "10px 0",
                    }}
                >
                    {accionExito}
                </div>
            )}
            {renderTabContent()}
            <Modal
                isOpen={!!accionPendiente}
                onClose={() => {
                    if (!ejecutandoAccion) setAccionPendiente(null);
                }}
                title={
                    accionPendiente
                        ? accionesObraConfig[accionPendiente.tipo].titulo
                        : ""
                }
            >
                {accionPendiente && (
                    <>
                        <p>
                            {accionesObraConfig[accionPendiente.tipo].texto(
                                accionPendiente.row
                            )}
                        </p>
                        <div className="modal-actions">
                            <Button
                                variant="outlined"
                                onClick={() => setAccionPendiente(null)}
                                disabled={ejecutandoAccion}
                                endIcon={undefined}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="filled"
                                onClick={ejecutarAccionObra}
                                loading={ejecutandoAccion}
                                endIcon={undefined}
                            >
                                {
                                    accionesObraConfig[accionPendiente.tipo]
                                        .confirmar
                                }
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
            <Modal
                isOpen={!!notaPendiente}
                onClose={cerrarNota}
                title={
                    confirmarBorrarNota
                        ? "Eliminar nota"
                        : notaPendiente?.obs2?.trim()
                          ? "Editar nota"
                          : "Añadir nota"
                }
            >
                {notaPendiente &&
                    (confirmarBorrarNota ? (
                        <>
                            <p>
                                ¿Seguro que deseas eliminar la nota de la obra{" "}
                                {notaPendiente.codEspacio}? Esta acción no se
                                puede deshacer.
                            </p>
                            <div className="modal-actions centro-nota__acciones">
                                <Button
                                    variant="outlined"
                                    onClick={() => setConfirmarBorrarNota(false)}
                                    disabled={guardandoNota}
                                    endIcon={undefined}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="filled"
                                    onClick={() => guardarNota("")}
                                    loading={guardandoNota}
                                    endIcon={undefined}
                                >
                                    Eliminar nota
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <InputTextarea
                                label={`Nota de la obra ${notaPendiente.codEspacio}`}
                                placeholder="Observaciones"
                                value={textoNota}
                                rows={4}
                                error={notaVacia}
                                disabled={guardandoNota}
                                onChange={(e) => {
                                    setTextoNota(
                                        e.target.value.slice(0, NOTA_MAX)
                                    );
                                    setNotaVacia(false);
                                }}
                            />
                            <p className="centro-nota__contador">
                                {textoNota.length}/{NOTA_MAX}
                            </p>
                            <div className="modal-actions centro-nota__acciones">
                                {notaPendiente.obs2?.trim() && (
                                    <Button
                                        variant="outlined"
                                        className="centro-nota__eliminar"
                                        onClick={() =>
                                            setConfirmarBorrarNota(true)
                                        }
                                        disabled={guardandoNota}
                                        endIcon={undefined}
                                    >
                                        Eliminar nota
                                    </Button>
                                )}
                                <Button
                                    variant="outlined"
                                    onClick={cerrarNota}
                                    disabled={guardandoNota}
                                    endIcon={undefined}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="filled"
                                    onClick={aceptarNota}
                                    loading={guardandoNota}
                                    endIcon={undefined}
                                >
                                    Aceptar
                                </Button>
                            </div>
                        </>
                    ))}
            </Modal>
        </PageLayout>
    );
}
